import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type ToastKind = 'info' | 'error';

type ToastState = {
  id: number;
  message: string;
  kind: ToastKind;
} | null;

type ToastContextValue = {
  showToast: (msg: string, kind?: ToastKind) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>(null);

  const showToast = useCallback((message: string, kind: ToastKind = 'info') => {
    setToast({ id: Date.now(), message, kind });
  }, []);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setToast(null);
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [toast]);

  const value = useMemo<ToastContextValue>(
    () => ({
      showToast,
    }),
    [showToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast ? (
        <button
          className={[
            'fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full px-4 py-3 text-sm shadow-2xl backdrop-blur-xl transition',
            toast.kind === 'error'
              ? 'border border-rose-400/30 bg-rose-500/20 text-rose-50'
              : 'border border-white/10 bg-white/10 text-white',
          ].join(' ')}
          type="button"
          onClick={() => setToast(null)}
        >
          {toast.message}
        </button>
      ) : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) {
    throw new Error('useToast must be used inside ToastProvider');
  }

  return value;
}
